import { useState } from "react";
import { BtnPrimary, Card, Input } from "../components/ui";
import { S, css } from "../theme";

function Biomarkers({ biomarkers, setBiomarkers }) {
  const [marker, setMarker] = useState("IGF-1");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("ng/mL");
  const [note, setNote] = useState("");

  const MARKERS = [
    { label: "IGF-1", unit: "ng/mL" },
    { label: "Growth Hormone", unit: "ng/mL" },
    { label: "CRP (Inflammation)", unit: "mg/L" },
    { label: "Testosterone", unit: "ng/dL" },
    { label: "HRV (Heart Rate Variability)", unit: "ms" },
    { label: "Sleep Quality", unit: "/10" },
    { label: "Energy Level", unit: "/10" },
    { label: "Pain Level", unit: "/10" },
    { label: "Body Weight", unit: "lbs" },
    { label: "Body Fat %", unit: "%" },
  ];

  function logEntry() {
    if (!value) return;
    setBiomarkers(prev => [{ id: Date.now(), marker, value: parseFloat(value), unit, note, date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }), timestamp: Date.now() }, ...prev]);
    setValue("");
    setNote("");
  }

  const grouped = MARKERS.map(m => ({
    label: m.label,
    entries: biomarkers.filter(b => b.marker === m.label).sort((a, b) => b.timestamp - a.timestamp),
  })).filter(g => g.entries.length > 0);

  return (
    <div style={{ padding: 40 }}>
      <h2 style={{ ...css.fontDisplay, fontSize: "1.8rem", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em", marginBottom: 8 }}>Biomarkers</h2>
      <p style={{ color: S.muted, fontSize: "0.85rem", marginBottom: 32 }}>Log blood work, subjective scores, and health metrics to track your progress over time.</p>

      <Card style={{ marginBottom: 32 }}>
        <h3 style={{ ...css.fontDisplay, fontWeight: 700, fontSize: "1.05rem", marginBottom: 20, color: S.ink }}>Log New Entry</h3>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: S.ink2, marginBottom: 6 }}>Marker</label>
            <select value={marker} onChange={e => { setMarker(e.target.value); setUnit(MARKERS.find(m => m.label === e.target.value)?.unit || ""); }} style={{ width: "100%", padding: "12px 16px", border: `1.5px solid ${S.border}`, borderRadius: 12, fontSize: "0.9rem", fontFamily: "'Inter', sans-serif", background: S.bg, color: S.ink, outline: "none" }}>
              {MARKERS.map(m => <option key={m.label} value={m.label}>{m.label}</option>)}
            </select>
          </div>
          <Input label={`Value (${unit})`} type="number" value={value} onChange={e => setValue(e.target.value)} placeholder="e.g. 180" />
        </div>
        <Input label="Notes (optional)" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Fasted, 8am draw" />
        <BtnPrimary onClick={logEntry} disabled={!value}>Log Entry →</BtnPrimary>
      </Card>

      {biomarkers.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 60 }}>
          <div style={{ fontSize: "3rem", marginBottom: 16 }}>📊</div>
          <div style={{ ...css.fontDisplay, fontSize: "1.3rem", fontWeight: 700, marginBottom: 8 }}>No biomarkers logged yet</div>
          <p style={{ color: S.muted, fontSize: "0.88rem" }}>Start logging your blood work and subjective scores above.</p>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {grouped.map(g => (
            <Card key={g.label}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: "1rem" }}>{g.label}</div>
                {g.entries.length > 1 && (
                  <div style={{ fontSize: "0.78rem", color: g.entries[0].value > g.entries[1].value ? S.accent : "#dc2626", fontWeight: 600 }}>
                    {g.entries[0].value > g.entries[1].value ? "↑" : "↓"} {Math.abs(g.entries[0].value - g.entries[1].value).toFixed(1)} {g.entries[0].unit}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                {g.entries.slice(0, 6).map(e => (
                  <div key={e.id} style={{ flexShrink: 0, background: S.bg, border: `1px solid ${S.border}`, borderRadius: 12, padding: "12px 16px", textAlign: "center", minWidth: 80 }}>
                    <div style={{ ...css.fontDisplay, fontSize: "1.3rem", fontWeight: 900, color: S.ink }}>{e.value}</div>
                    <div style={{ fontSize: "0.65rem", color: S.muted }}>{e.unit}</div>
                    <div style={{ fontSize: "0.68rem", color: S.muted, marginTop: 4 }}>{e.date}</div>
                  </div>
                ))}
              </div>
              {g.entries[0].note && <p style={{ fontSize: "0.75rem", color: S.muted, marginTop: 10, marginBottom: 0 }}>Note: {g.entries[0].note}</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PEPTIDE LIBRARY ─────────────────────────────────────────

export { Biomarkers };
